import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import { doc, updateDoc, increment, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast';

// 🔥 IMPORT SOUNDS & PIECES
import { PIECE_SETS, BOARD_THEMES, playSound } from '../Board/BoardConstants';

const sanitizeMove = (m) => {
    if (!m) return '';
    return m.replace(/[+#x\-]/g, '').trim().toLowerCase();
};

export default function HomeworkSolver({ homework, userProfile, goBack }) {
    const boardTheme = userProfile?.boardTheme || 'green';
    const pieceTheme = userProfile?.pieceTheme || 'cburnett';
    const currentPieceSet = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;
    const { light: lightSquareColor, dark: darkSquareColor } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;

    const [currentTaskIdx, setCurrentTaskIdx] = useState(0);
    const [fen, setFen] = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
    const [selectedSquare, setSelectedSquare] = useState(null);
    
    // 🔥 NEW STATE: Added Legal Moves to show Dots!
    const [legalMoves, setLegalMoves] = useState([]);
    
    const [expectedMoves, setExpectedMoves] = useState([]);
    const [currentMoveIdx, setCurrentMoveIdx] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [boardWidth, setBoardWidth] = useState(400);

    const materials = homework.materials || [];

    useEffect(() => {
        setBoardWidth(Math.min(window.innerHeight - 250, window.innerWidth - 40, 500));
        loadTask(0);
    }, [homework]);

    const loadTask = (idx) => {
        if (idx >= materials.length) {
            finishHomework();
            return;
        }
        setCurrentTaskIdx(idx);
        const task = materials[idx];
        
        const taskFen = task.fen || task.content || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        setFen(taskFen);
        
        const answerStr = task.solution || task.answer || '';
        const ans = answerStr ? answerStr.split(',').map(m => m.trim()) : [];
        setExpectedMoves(ans);
        
        setCurrentMoveIdx(0);
        setSelectedSquare(null);
        setLegalMoves([]); // Reset dots on new puzzle
    };

    const logStudentMove = async (playedMove, isCorrect) => {
        try {
            const hwRef = doc(db, "homeworks", homework.id);
            const task = materials[currentTaskIdx];
            
            await updateDoc(hwRef, {
                activityLogs: arrayUnion({
                    studentEmail: userProfile.email,
                    studentName: userProfile.name,
                    taskIndex: currentTaskIdx + 1,
                    taskDesc: task.desc || task.title || "Puzzle",
                    playedMove: playedMove,
                    expectedMove: expectedMoves[currentMoveIdx] || '',
                    isCorrect: isCorrect,
                    timestamp: new Date().toISOString()
                })
            });
        } catch (error) {
            console.error("X-Ray Log Failed:", error);
        }
    };

    const finishHomework = async () => {
        setCompleted(true);
        playSound('notify');
        confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 }, zIndex: 9999 });
        toast.success("Homework Completed! +20 XP 🏆");

        try {
            const hwRef = doc(db, "homeworks", homework.id);
            await updateDoc(hwRef, { completedBy: arrayUnion(userProfile.email) });

            const userRef = doc(db, "users", userProfile.email);
            await updateDoc(userRef, { totalXp: increment(20) });
        } catch(e) {}
    };

    // 🔥 NEW ADVANCED MOVE ENGINE (Same as Classroom/Arena)
    const attemptMove = (from, to) => {
        if (completed) return;
        let game;
        try { game = new Chess(fen); } catch(e) { return; }

        let move = null;
        try { move = game.move({ from, to, promotion: 'q' }); } catch(e){}

        if (move) {
            if (expectedMoves.length > 0 && currentMoveIdx < expectedMoves.length) {
                const cleanExpected = sanitizeMove(expectedMoves[currentMoveIdx]);
                const cleanPlayedSan = sanitizeMove(move.san);
                const cleanPlayedUci = sanitizeMove(move.from + move.to);

                if (cleanPlayedSan === cleanExpected || cleanPlayedUci === cleanExpected) {
                    setFen(game.fen());
                    setSelectedSquare(null);
                    setLegalMoves([]);
                    logStudentMove(move.san, true); 
                    
                    const nextIdx = currentMoveIdx + 1;
                    setCurrentMoveIdx(nextIdx);

                    if (nextIdx >= expectedMoves.length) {
                        playSound('notify'); 
                        toast.success("Correct! 🎯");
                        setTimeout(() => loadTask(currentTaskIdx + 1), 1000);
                    } else {
                        playSound('notify'); 
                        setTimeout(() => {
                            const autoGame = new Chess(game.fen());
                            try {
                                autoGame.move(expectedMoves[nextIdx]);
                                setFen(autoGame.fen());
                                playSound('move'); 
                                
                                const nextNextIdx = nextIdx + 1;
                                setCurrentMoveIdx(nextNextIdx);
                                if (nextNextIdx >= expectedMoves.length) {
                                    setTimeout(() => {
                                        playSound('notify');
                                        toast.success("Correct! 🎯");
                                        setTimeout(() => loadTask(currentTaskIdx + 1), 1000);
                                    }, 500);
                                }
                            } catch(e) {
                                toast.success("Correct! 🎯");
                                setTimeout(() => loadTask(currentTaskIdx + 1), 1000);
                            }
                        }, 600);
                    }
                } else {
                    playSound('error'); 
                    toast.error(`❌ Wrong Move! You played ${move.san}`);
                    logStudentMove(move.san, false); 
                    setSelectedSquare(null);
                    setLegalMoves([]);
                }
            } else {
                setFen(game.fen());
                playSound('move');
                setSelectedSquare(null);
                setLegalMoves([]);
            }
        }
    };

    // 🔥 NEW CLICK HANDLER: Selects piece, shows dots, or executes move
    const handleSquareClick = (id) => {
        if (completed) return;
        let game;
        try { game = new Chess(fen); } catch (e) { return; }

        // If clicking a valid dot
        if (legalMoves.includes(id) && selectedSquare) {
            attemptMove(selectedSquare, id);
        } else {
            // Select a new piece
            const piece = game.get(id);
            if (piece && piece.color === game.turn()) {
                setSelectedSquare(id);
                // Calculate and show dots
                const moves = game.moves({ square: id, verbose: true }).map(m => m.to);
                setLegalMoves(moves);
            } else {
                setSelectedSquare(null);
                setLegalMoves([]);
            }
        }
    };

    const currentTask = materials[currentTaskIdx];
    if (!currentTask && !completed) return <div style={{color:'var(--text-primary)'}}>Loading...</div>;

    const rows = [8,7,6,5,4,3,2,1]; 
    const cols = ['a','b','c','d','e','f','g','h'];
    
    const renderBoard = {};
    try {
        const boardRows = fen.split(' ')[0].split('/');
        boardRows.forEach((rowStr, r) => {
            let colIdx = 0;
            for (let i = 0; i < rowStr.length; i++) {
                const char = rowStr[i];
                if (isNaN(char)) {
                    const color = char === char.toUpperCase() ? 'w' : 'b';
                    renderBoard[`${cols[colIdx]}${rows[r]}`] = color + char.toUpperCase();
                    colIdx++;
                } else {
                    colIdx += parseInt(char);
                }
            }
        });
    } catch(e) {}

    return (
        <div style={{ width: '100%', maxWidth: '1000px', margin: '0 auto', paddingBottom: '40px' }}>
            <div className="glass-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-primary)', padding: '20px 30px', borderRadius: '28px', border: '3px solid rgba(255, 255, 255, 0.95)', marginBottom: '30px', boxShadow: 'var(--panel-shadow)' }}>
                <div>
                    <h2 style={{ margin: '0 0 5px 0', color: 'var(--text-primary)', fontWeight: '900' }}>{homework.title}</h2>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold' }}>Assigned by: {homework.assignedBy}</div>
                </div>
                <button onClick={goBack} className="kid-btn-bubble" style={{ padding: '10px 24px', background: '#ef4444', color: '#fff', border: '3px solid #fff', borderRadius: '50px', fontWeight: '900', cursor: 'pointer', fontSize: '14px', boxShadow: 'var(--neu-shadow)' }}>✖ Exit</button>
            </div>

            {completed ? (
                <div className="glass-panel" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '50px 30px', borderRadius: '28px', border: '3px solid rgba(255, 255, 255, 0.95)', textAlign: 'center', color: '#ffffff', boxShadow: '0 15px 35px rgba(16, 185, 129, 0.3)' }}>
                    <div className="animate-float-cute" style={{ fontSize: '80px', marginBottom: '20px' }}>🎉</div>
                    <h1 style={{ margin: '0 0 10px 0', fontSize: '36px', fontWeight: '900', textShadow: '2px 3px 0px #047857, 0 10px 20px rgba(0,0,0,0.2)' }}>Brilliant Work!</h1>
                    <p style={{ fontSize: '18px', opacity: 0.9, fontWeight: '700', marginBottom: '25px' }}>You completed the assignment and earned 20 XP!</p>
                    <button onClick={goBack} className="kid-btn-bubble" style={{ padding: '14px 32px', background: '#ffffff', color: '#059669', fontSize: '16px', fontWeight: '900', border: '3px solid #fff', borderRadius: '50px', cursor: 'pointer', boxShadow: 'var(--neu-shadow)' }}>Return to Dashboard</button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                    <div className="glass-panel" style={{ width: `${boardWidth}px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', background: 'var(--bg-primary)', padding: '15px 20px', borderRadius: '20px', border: '3px solid rgba(255, 255, 255, 0.95)', boxShadow: 'var(--neu-shadow)' }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800' }}>Puzzle {currentTaskIdx + 1} of {materials.length}</span>
                        <span style={{ color: '#10b981', fontSize: '18px', fontWeight: '900' }}>{currentTask.desc || currentTask.title || "Solve this"}</span>
                    </div>

                    <div style={{ width: `${boardWidth}px`, aspectRatio: '1/1', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)`, border: '8px solid var(--bg-primary)', borderRadius: '24px', overflow: 'hidden', boxShadow: 'var(--panel-shadow)', position: 'relative' }}>
                        {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                            const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                            return (
                                <div key={id} onClick={() => handleSquareClick(id)} 
                                    onDragOver={e => e.preventDefault()} 
                                    onDrop={e => {
                                        const fromSq = e.dataTransfer.getData('fromSq');
                                        if(fromSq) attemptMove(fromSq, id);
                                    }} 
                                    style={{position: 'relative', background: selectedSquare === id ? '#2563eb' : (isDark ? darkSquareColor : lightSquareColor), display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    
                                    {/* 🔥 THE DOTS ENGINE */}
                                    {legalMoves.includes(id) && (
                                        <div style={{width: '30%', height: '30%', background: 'rgba(0,0,0,0.3)', borderRadius: '50%', position: 'absolute', zIndex: 5, pointerEvents: 'none'}} />
                                    )}
                                </div>
                            );
                        }))}
                        
                        {Object.entries(renderBoard).map(([id, piece]) => {
                            const cIdx = cols.indexOf(id[0]); 
                            const rIdx = rows.indexOf(parseInt(id[1]));
                            if (!currentPieceSet[piece]) return null;
                            
                            let game; try { game = new Chess(fen); } catch(e){}
                            // Only allow grabbing if it's that color's turn
                            const isMyTurn = game && game.turn() === piece[0];

                            return (
                                <img key={id} src={currentPieceSet[piece]} alt={piece} draggable={isMyTurn}
                                    onDragStart={e => e.dataTransfer.setData('fromSq', id)}
                                    onClick={(e) => { e.stopPropagation(); handleSquareClick(id); }}
                                    style={{ position: 'absolute', width: '12.5%', height: '12.5%', left: `${cIdx * 12.5}%`, top: `${rIdx * 12.5}%`, cursor: isMyTurn ? 'grab' : 'default', zIndex: 10, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))' }} 
                                />
                            );
                        })}
                    </div>

                    {expectedMoves.length === 0 && (
                        <button onClick={() => { loadTask(currentTaskIdx + 1); }} className="kid-btn-bubble" style={{ padding: '14px 32px', background: '#3b82f6', color: 'white', fontWeight: '900', border: '3px solid #fff', borderRadius: '50px', cursor: 'pointer', fontSize: '16px', boxShadow: 'var(--neu-shadow)' }}>
                            Mark as Understood & Next ▶️
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
